<template>
	<div class="esg" @click="handleRating(item)">
		<div class="rating-header">
			<div class="rating-value">
				{{ item.rate || item.score }}
			</div>
			<div class="indicator-time">
				<p class="indicator">{{ item.orgName }}</p>
				<p class="time">{{ item.date }}</p>
			</div>
		</div>
		<template v-if="item.esgRateRatioVOList">
			<div class="rating-list">
				<p
					:class="{ 'active-rating': rating.rate === item.rate }"
					v-for="rating in item.reverseEsgRateRatioVOList"
					:key="rating.rate"
				>
					{{ rating.rate }}
				</p>
			</div>

			<div class="rating-distribution" v-if="!isOverview">
				<div
					class="distribution-item"
					:class="{ 'active-distribution': rating.rate === item.rate }"
					v-for="rating in item.esgRateRatioVOList"
					:key="rating.rate"
				>
					<div class="distribution-left">{{ rating.rate }}</div>
					<div class="distribution-right">
						<div class="distribution-ratio">{{ rating.rateNumRatio }}%</div>
						<div
							class="distribution-pie"
							:style="getPieBg(`${rating.rateNumRatio}`, rating.rate === item.rate)"
						></div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup>
import { hexToRgb } from '@/utils/api';
import { useCommonData } from '@/hooks';

const { getUpperThemeColor } = useCommonData();

const props = defineProps({
	item: {
		type: Object,
		default() {
			return {};
		}
	},
	isOverview: {
		type: Boolean,
		default: false
	}
});

function getPieBg(value, active) {
	const _value = Math.max(parseFloat(value), 1.5) + '%';
	const emphasisColor = active ? '#fff' : getUpperThemeColor.value;
	const emphasisColorRGB = hexToRgb(emphasisColor);
	const normalColor = `rgba(${emphasisColorRGB.r}, ${emphasisColorRGB.g}, ${emphasisColorRGB.b}, ${
		active ? 0.2 : 0.1
	})`;
	return {
		background: `conic-gradient(
            ${emphasisColor} 0% ${_value},
            ${normalColor} ${_value} 100%
        )`
	};
}

const emits = defineEmits(['handleRating']);
const handleRating = item => {
	emits('handleRating', item);
};
</script>

<style lang="less" scoped>
.esg {
	padding: 12px;
	border-radius: 4px;
	background-color: var(--theme-color-gradient-010);

	.rating-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 38px;
	}

	.rating-value {
		height: 30px;
		padding: 0 8px;
		border-radius: 2px;
		background: var(--theme-color);

		color: #fff;
		font-family: 'DIN Alternate';
		font-size: 20px;

		font-weight: 700;
		line-height: 30px; /* 130% */
	}

	.indicator {
		color: var(--t-emphasis-color, #1d2129);
		text-align: right;

		font-size: 14px;

		font-weight: 600;
		line-height: normal;
		/* 120% */
	}

	.time {
		color: var(--t-secondary-info-color, #86909c);
		text-align: right;

		font-size: 13px;

		font-weight: 400;
		line-height: normal;
	}

	.rating-list {
		margin-top: 16px;
		height: 24px;
		padding: 0 12px;
		border-radius: 1px;
		border: 0.5px solid var(--theme-color);

		display: flex;
		justify-content: space-between;

		color: var(--theme-color);

		font-size: 12px;

		font-weight: 400;
		line-height: 24px;

		.active-rating {
			padding: 0 6px;
			background: var(--theme-color);
			color: #fff;
		}
	}

	.rating-distribution {
		margin-top: 12px;

		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-gap: 8px;

		.distribution-item {
			min-width: 0;
			height: 24px;
			padding: 0 8px;
			border-radius: 2px;
			background: rgba(0, 0, 0, 0.03);
			display: flex;
			align-items: center;
			justify-content: space-between;

			color: #4e5969;

			font-size: 12px;

			font-weight: 400;

			&.active-distribution {
				background: var(--theme-color);
			}

			.distribution-right {
				display: flex;
				align-items: center;

				font-family: 'DIN Alternate';
				font-weight: 700;
			}

			.distribution-pie {
				margin-left: 8px;

				width: 16px;
				height: 16px;
				border-radius: 50%;
			}

			&.active-distribution {
				background: var(--theme-color);
				color: #fff;
			}
		}
	}
}
</style>
